/**
 * Pre-built decisions that demonstrate each decision-science mechanic.
 * Used by the "Open in builder" CTAs on /how-it-works.
 *
 * Each seed function returns a complete decision (without id/timestamps) ready to pass
 * to `useStore().addDecision()`. The returned id is then used for navigation.
 */
import type { Choice, Variable, WealthConfig } from '../types'
import { uid } from '../store'

const N = (mu: number, sigma: number) => ({ kind: 'Normal' as const, params: { mu, sigma } })
const LN = (mu: number, sigma: number) => ({ kind: 'LogNormal' as const, params: { mu, sigma } })
const B = (p: number) => ({ kind: 'Bernoulli' as const, params: { p } })
const Det = () => ({ kind: 'Normal' as const, params: { mu: 0, sigma: 0.0001 } })

const v = (
  name: string,
  role: Variable['role'],
  time: Variable['time'],
  units: string,
  dist: any,
): Variable => ({
  id: uid(),
  name,
  type: 'parameter',
  role,
  time,
  units,
  dist,
})

const ch = (name: string, notes: string, variables: Variable[]): Choice => ({
  id: uid(),
  name,
  notes,
  variables,
})

export type ExplainerKind = 'monte_carlo' | 'bayes' | 'sensitivity' | 'risk' | 'agent'
export type TemplateSlug = 'should-i-take-the-new-job' | 'masters-vs-bootcamp-vs-stay' | 'should-i-move-cities' | 'buy-vs-rent' | 'go-freelance-vs-stay-employed'

interface SeedDecision {
  name: string
  horizon_years: number
  discount_rate: number
  risk_aversion: number
  choices: Choice[]
  wealth?: WealthConfig
}

/** The "future me" hand-off from the Should I Quit tool. A long-horizon,
 *  wealth-mode stay-vs-new-job decision: take-home pay compounds into a
 *  net-worth trajectory and an earliest-retirement estimate — the thing the
 *  quick tool structurally can't show. Parameterised by the tool's growth pace
 *  (raise cadence) and feel level so it continues the user's scenario. */
export function buildQuitWealthSeed(opts: { growthIdx?: number; feelIdx?: number } = {}): SeedDecision {
  const growthRates = [0.01, 0.03, 0.05]                 // Slower / Typical / Faster (real income growth)
  const income_growth = growthRates[Math.max(0, Math.min(2, opts.growthIdx ?? 1))]
  const feelToSat = [5, 6, 7, 8, 9]                       // feel 0-4 → new-job satisfaction
  const newSat = feelToSat[Math.max(0, Math.min(4, opts.feelIdx ?? 2))]
  return {
    name: 'Should I quit — the long-run view',
    horizon_years: 30,                                    // retirement needs decades
    discount_rate: 0.05,
    risk_aversion: 0.002,
    wealth: {
      enabled: true,
      starting_balance: 20_000,
      annual_expenses: 35_000,
      real_return: 0.05,
      income_growth,
      withdrawal_rate: 0.04,
    },
    choices: [
      ch('Stay at current job', 'Familiar, stable, slower growth.', [
        v('Take-home pay', 'income', 'per_step', '€/year', N(48_000, 3_000)),
        v('Layoff risk', 'risk', 'per_step', 'prob/year', B(0.05)),
        v('Job satisfaction (1-10)', 'wellbeing', 'per_step', 'score', N(6, 1.5)),
      ]),
      ch('Take the new job', 'Higher pay, new environment, some uncertainty.', [
        v('Take-home pay', 'income', 'per_step', '€/year', N(60_000, 4_000)),
        v('Layoff risk', 'risk', 'per_step', 'prob/year', B(0.12)),
        v('Relocation cost', 'one_time_cost', 'once', '€', N(3_000, 1_000)),
        v('Job satisfaction (1-10)', 'wellbeing', 'per_step', 'score', N(newSat, 1.5)),
      ]),
    ],
  }
}

/** Build a fresh seed decision for the given explainer. */
export function buildExplainerSeed(kind: ExplainerKind): SeedDecision {
  switch (kind) {
    case 'monte_carlo':
      // Classic 2-option job decision — runs a clean Monte Carlo with visible bell curves.
      return {
        name: 'Demo · Take the new job?',
        horizon_years: 5,
        discount_rate: 0.05,
        risk_aversion: 0.002,
        choices: [
          ch('Stay at current job', 'Familiar role, slower growth.', [
            v('Salary', 'income', 'per_step', '€/year', N(52_000, 4_000)),
            v('Annual raise', 'income', 'cumulative', '€/year', N(1_800, 800)),
            v('Living costs', 'cost', 'per_step', '€/year', N(28_000, 2_500)),
          ]),
          ch('Take the new offer', 'Higher base, more upside, less certainty.', [
            v('Salary', 'income', 'per_step', '€/year', N(68_000, 7_500)),
            v('Sign-on bonus', 'one_time_income', 'once', '€', N(7_000, 1_500)),
            v('Living costs', 'cost', 'per_step', '€/year', N(31_000, 3_000)),
            v('Layoff risk', 'risk', 'per_step', 'prob/year', B(0.07)),
          ]),
        ],
      }
    case 'bayes':
      // One option, one fuzzy salary you can later Bayesian-update on Results.
      return {
        name: 'Demo · Bayesian salary update',
        horizon_years: 3,
        discount_rate: 0.05,
        risk_aversion: 0.002,
        choices: [
          ch('Apply to startup', "Salary is your prior. Interview will give you a posterior.", [
            v('Salary', 'income', 'per_step', '€/year', N(58_000, 12_000)),
            v('Equity grant (value at exit)', 'one_time_income', 'once', '€', LN(10.1, 1.0)),
            v('Living costs', 'cost', 'per_step', '€/year', N(30_000, 2_500)),
          ]),
        ],
      }
    case 'sensitivity':
      // Many variables across two options — the Tornado will rank them by impact.
      return {
        name: 'Demo · Which variables actually matter?',
        horizon_years: 5,
        discount_rate: 0.05,
        risk_aversion: 0.002,
        choices: [
          ch('Move to a new city', 'Higher pay, higher rent, layoff risk.', [
            v('Salary', 'income', 'per_step', '€/year', N(72_000, 9_000)),
            v('Annual raise', 'income', 'cumulative', '€/year', N(2_400, 1_200)),
            v('Rent', 'cost', 'per_step', '€/year', N(22_000, 4_000)),
            v('Moving cost', 'one_time_cost', 'once', '€', N(4_500, 1_200)),
            v('Layoff risk', 'risk', 'per_step', 'prob/year', B(0.08)),
          ]),
          ch('Stay where you are', 'Lower pay, lower rent, very stable.', [
            v('Salary', 'income', 'per_step', '€/year', N(55_000, 2_500)),
            v('Annual raise', 'income', 'cumulative', '€/year', N(1_800, 500)),
            v('Rent', 'cost', 'per_step', '€/year', N(13_000, 900)),
          ]),
        ],
      }
    case 'risk':
      // Two options engineered to expose CARA. Same mean, different volatility.
      return {
        name: 'Demo · Risk style flips the winner',
        horizon_years: 3,
        discount_rate: 0.05,
        risk_aversion: 0.005, // start risk-averse so the safe option wins
        choices: [
          ch('Safe (steady gig)', 'Boring but predictable.', [
            v('Income', 'income', 'per_step', '€/year', N(60_000, 3_000)),
          ]),
          ch('Risky (commission role)', 'High variance, same average.', [
            v('Income', 'income', 'per_step', '€/year', N(60_000, 22_000)),
          ]),
        ],
      }
    case 'agent':
      // Agent doesn't need a decision pre-built; route handler will simply navigate.
      return {
        name: '',
        horizon_years: 0,
        discount_rate: 0,
        risk_aversion: 0,
        choices: [],
      }
  }
}

/**
 * Meta-seed: "Should I rebrand LifeOdds?" — used by the hidden `/app?seed=rebrand` URL.
 * Each choice is a finalist name from the brand brainstorm. The variables encode the rough
 * trade-offs we discussed: word-of-mouth upside, domain cost, rebrand effort, SEO crowding,
 * misread risk (gambling vs. wellness misread), and a brand-pride wellbeing tile.
 *
 * Numbers are intentionally rough — the user should tweak them in the builder. They're
 * calibrated so the simulation produces a meaningful spread, not a tie.
 */
export function buildRebrandSeed(): SeedDecision {
  return {
    name: 'Should I rebrand LifeOdds?',
    horizon_years: 3,
    discount_rate: 0.10,
    risk_aversion: 0.003,
    choices: [
      ch(
        'LifeOdds (keep)',
        'Status quo. Zero rebrand cost, but the dice/gambling read may cap top-of-funnel growth.',
        [
          v('Annual word-of-mouth revenue', 'income', 'per_step', '€/year', N(50_000, 8_000)),
          v('Domain cost (already owned)', 'one_time_cost', 'once', '€', Det()),
          v('Rebrand effort (none)', 'one_time_cost', 'once', '€', Det()),
          v('SEO crowding (penalty)', 'cost', 'per_step', '€/year', N(1_500, 1_000)),
          v('Misread-as-gambling risk', 'risk', 'per_step', 'prob/year', B(0.25)),
          v('Brand pride (1-10)', 'wellbeing', 'per_step', 'score', N(6.5, 1.5)),
        ],
      ),
      ch(
        'LifeBranch',
        'Safe step sideways. Continuity from LifeOdds, evokes the fork/lineage features.',
        [
          v('Annual word-of-mouth revenue', 'income', 'per_step', '€/year', N(55_000, 10_000)),
          v('Domain cost', 'one_time_cost', 'once', '€', LN(7.6, 0.8)),
          v('Rebrand effort', 'one_time_cost', 'once', '€', N(5_000, 1_500)),
          v('SEO crowding (penalty)', 'cost', 'per_step', '€/year', N(8_000, 3_000)),
          v('Misread-as-banking-app risk', 'risk', 'per_step', 'prob/year', B(0.15)),
          v('Brand pride (1-10)', 'wellbeing', 'per_step', 'score', N(7.0, 1.2)),
        ],
      ),
      ch(
        'FutureSelf',
        'Emotional, owns the future-self-continuity psychology angle. Wellness-misread risk.',
        [
          v('Annual word-of-mouth revenue', 'income', 'per_step', '€/year', N(70_000, 18_000)),
          v('Domain cost (premium .com)', 'one_time_cost', 'once', '€', LN(9.6, 1.0)),
          v('Rebrand effort', 'one_time_cost', 'once', '€', N(7_000, 2_000)),
          v('SEO crowding (penalty)', 'cost', 'per_step', '€/year', N(4_000, 2_000)),
          v('Misread-as-wellness risk', 'risk', 'per_step', 'prob/year', B(0.35)),
          v('Brand pride (1-10)', 'wellbeing', 'per_step', 'score', N(8.2, 1.0)),
        ],
      ),
      ch(
        'Hindsight',
        'Highest upside concept — tagline writes itself ("Hindsight, in advance."). Expensive .com.',
        [
          v('Annual word-of-mouth revenue', 'income', 'per_step', '€/year', N(85_000, 22_000)),
          v('Domain cost (popular word)', 'one_time_cost', 'once', '€', LN(10.1, 1.2)),
          v('Rebrand effort', 'one_time_cost', 'once', '€', N(7_000, 2_000)),
          v('SEO crowding (penalty)', 'cost', 'per_step', '€/year', N(5_000, 2_500)),
          v('Misread risk (very low)', 'risk', 'per_step', 'prob/year', B(0.05)),
          v('Brand pride (1-10)', 'wellbeing', 'per_step', 'score', N(9.0, 0.8)),
        ],
      ),
      ch(
        'Whittle',
        'Warm, tactile, distinctive. Possibly available .com. Risk: feels small for big decisions.',
        [
          v('Annual word-of-mouth revenue', 'income', 'per_step', '€/year', N(62_000, 14_000)),
          v('Domain cost', 'one_time_cost', 'once', '€', LN(8.2, 0.9)),
          v('Rebrand effort', 'one_time_cost', 'once', '€', N(6_000, 1_500)),
          v('SEO crowding (penalty)', 'cost', 'per_step', '€/year', N(2_500, 1_500)),
          v('Misread-as-folksy risk', 'risk', 'per_step', 'prob/year', B(0.15)),
          v('Brand pride (1-10)', 'wellbeing', 'per_step', 'score', N(7.5, 1.3)),
        ],
      ),
    ],
  }
}

// ─── Sourced Master's assumptions (homepage example + /decisions page) ───────
// Every number here has a source; see MASTERS_SOURCES. The engine has no
// "salary starts in year 3", so two study years are modelled as a one-off cost
// of two years of post-degree pay set against a post-degree salary from year 1:
// the same total, slightly earlier (≈3% NPV difference at a 5% discount rate).
const BASE_SALARY = 55_000            // illustrative bachelor's salary: replace with yours
const MASTERS_PREMIUM = 0.13          // StepStone graduate salary report: master's start ~13% higher
const POST_MASTERS_SALARY = Math.round(BASE_SALARY * (1 + MASTERS_PREMIUM) / 50) * 50

export const MASTERS_SOURCES: { label: string; url: string }[] = [
  { label: "StepStone graduate salary report 2019/20 (~11,000 graduates): master's graduates start ~13% above bachelor's",
    url: 'https://www.tageskarte.io/zahlen/detail/einstiegsgehaelter-fuer-absolventen-masterabschluss-bringt-13-prozent-mehr.html' },
  { label: 'German public universities charge no tuition for most students; the semester contribution is mostly €120–430',
    url: 'https://karrierebibel.de/semesterbeitrag/' },
  { label: "Cross-check (US, all ages): BLS median weekly earnings 2025, master's $1,876 vs bachelor's $1,578 (+19%)",
    url: 'https://www.bls.gov/emp/tables/unemployment-earnings-education.htm' },
]

const MASTERS_NOTE =
  'Two years studying at a German public university: no tuition, four semester contributions, no salary. ' +
  'Afterwards ~13% more than with a bachelor\'s (StepStone graduate salary report).'

const mastersVariables = (): Variable[] => [
  v('Semester contributions (4 semesters)', 'one_time_cost', 'once', '€', N(1_200, 400)),
  v('Two years without salary (studying)', 'one_time_cost', 'once', '€', N(POST_MASTERS_SALARY * 2, POST_MASTERS_SALARY * 0.2)),
  v("Salary after the Master's (+13%)", 'income', 'per_step', '€/year', N(POST_MASTERS_SALARY, 7_000)),
  v('Annual raise', 'income', 'cumulative', '€/year', N(Math.round(2_000 * (1 + MASTERS_PREMIUM)), 900)),
]

// ─── Sourced bootcamp assumptions (/decisions/masters-vs-bootcamp-vs-stay) ───
// A bootcamp is a career switch: graduates restart as junior developers, so the
// salary is a junior developer's, not a premium on the current one. Le Wagon's
// Germany figure is a self-reported median of job-seeking respondents (USD,
// converted at the 2021 average of ~€0.85/$); get in IT's bachelor's entry band
// for web developers is the independent cross-check.
const BOOTCAMP_SALARY = 44_000        // Le Wagon DE median $51K ≈ €43K; get in IT entry band €40.2–46.3K

const BOOTCAMP_SOURCES: { label: string; url: string }[] = [
  { label: 'Le Wagon jobs report 2021 (via Career Karma): Germany graduates found a job in a median 37 days at a median salary of $51,000 (≈€43K, self-reported)',
    url: 'https://careerkarma.com/blog/is-le-wagon-bootcamp-worth-it/' },
  { label: "get in IT: entry salary for web developers in Germany with a bachelor's is €40,200–46,300 (middle 50%)",
    url: 'https://www.get-in-it.de/magazin/gehalt/gehalt-webentwickler' },
  { label: 'Independent check of Le Wagon London graduates on LinkedIn: ~56–70% in a developer job vs the 86–90% reported',
    url: 'https://lewagonfiles.substack.com/p/testing-le-wagons-job-placement-data' },
]

const BOOTCAMP_NOTE =
  'A career switch: you restart as a junior developer (~€44K in Germany, Le Wagon / get in IT), ' +
  'below a €55K bachelor\'s salary, and catch up only through faster raises.'

// ─── Sourced assumptions for the other /decisions pages ─────────────────────
// Each block below backs one /decisions page; every number traces to the
// matching *_SOURCES entry, and what has no source is named in the page's
// `unsourced` text. Germany as the worked example throughout.

// The engine's risk role wipes 60% of a year's income when it fires. A German
// job loss costs far less on average (BA: 0.59% of employees a month register
// unemployed; ALG I pays 60–67% of net pay and the average spell on it is
// 17–25 weeks, so ≈1% of a year's income is lost per year), so the layoff
// chance is the one that gives that expected loss: 1% / 60% ≈ 2% a year.
const LAYOFF_P = 0.02

const JOB_SOURCES: { label: string; url: string }[] = [
  { label: 'IAB (2023): of voluntary job switchers, 47% gain over 5%, about a quarter end up worse off',
    url: 'https://iab-forum.de/nicht-alle-jobwechsel-fuehren-zu-lohnsteigerungen/' },
  { label: 'Bertelsmann Stiftung (2025, SOEP): switchers in the same occupation earn +3.6%, and rate the new job 1.1 points (of 10) more satisfying',
    url: 'https://www.bertelsmann-stiftung.de/de/themen/aktuelle-meldungen/2025/januar/unzufrieden-im-job-mut-zum-wechsel-zahlt-sich-aus' },
  { label: 'Chadi & Hetschko (SOEP): the satisfaction boost after a switch is short-lived',
    url: 'https://www.wiwiss.fu-berlin.de/fachbereich/vwl/schoeb/forschung/diskussionspapiere/chadi-hetschko-2014.pdf' },
  { label: 'Destatis: nominal wages +4.2% in 2025 (raises in this scenario)',
    url: 'https://www.destatis.de/DE/Presse/Pressemitteilungen/2026/02/PD26_068_62321.html' },
  { label: 'Bundesagentur für Arbeit (Dec 2025): 0.59% of employees a month register unemployed; ALG I spells average 17–25 weeks',
    url: 'https://statistik.arbeitsagentur.de/Statistikdaten/Detail/202512/arbeitsmarktberichte/monatsbericht-monatsbericht/monatsbericht-d-0-202512-pdf.pdf?__blob=publicationFile&v=1' },
  { label: 'Bundesagentur für Arbeit, job-duration report (2016): of the jobs that ended in 2014, 55% had lasted under a year (all reasons: quits, dismissals, contracts running out), while only 19% of running jobs were under a year old',
    url: 'https://statistik.arbeitsagentur.de/DE/Statischer-Content/Grundlagen/Methodik-Qualitaet/Methodenberichte/Beschaeftigungsstatistik/Generische-Publikationen/Methodenbericht-Dauer-Beschaeftigungsverhaeltnissen.pdf?__blob=publicationFile' },
]

// Leipzig → Munich. Take-home pay is an approximation from gross (single,
// tax class I, statutory insurance), not a source figure; see `unsourced`.
const LEIPZIG_GROSS = 3_784 * 12      // BA median full-time gross, Leipzig (2024)
const MUNICH_GAP = 5_362 / 3_784 - 1  // BA medians: Munich ≈ 42% above Leipzig
const MOVER_SHARE = 0.46              // Dauth et al.: wage elasticity 3.7% → 1.7% with worker fixed effects
const MUNICH_GROSS = Math.round(LEIPZIG_GROSS * (1 + MUNICH_GAP * MOVER_SHARE) / 100) * 100
const FLAT_M2 = 65
// A stayer keeps an existing lease, so Leipzig is the Mietspiegel average (rents
// set or changed in the last six years); the mover pays Munich's new-contract rent.
const LEIPZIG_RENT = Math.round(6.56 * FLAT_M2 * 12 / 100) * 100   // Leipzig Mietspiegel 2025–2027
const MUNICH_RENT = Math.round(19.92 * FLAT_M2 * 12 / 100) * 100
const RENT_GROWTH = 0.021             // Destatis CPI net rents 2025
const WAGE_GROWTH = 0.042             // Destatis nominal wages 2025

const MOVE_SOURCES: { label: string; url: string }[] = [
  { label: 'Bundesagentur für Arbeit (2024): median full-time gross pay €5,362/month in Munich, €4,013 nationally',
    url: 'https://www.arbeitsagentur.de/vor-ort/datei/2025-46-entgelt-landkreis-ebersberg_ba247153.pdf' },
  { label: 'Leipziger Internet Zeitung, citing the Leipzig employment agency: median €3,784/month in Leipzig',
    url: 'https://www.l-iz.de/politik/kassensturz/2025/07/deutschlandweiter-vergleich-medianlohn-leipzig-mittelfeld-630221' },
  { label: 'Dauth, Findeisen, Moretti & Suedekum (IZA 2019): most of the big-city wage premium is who lives there; a mover gains under half of it',
    url: 'https://docs.iza.org/dp12278.pdf' },
  { label: 'ImmoScout24 WohnBarometer Q4 2025: asking rents €19.92/m² in Munich, €8.85/m² in Leipzig',
    url: 'https://www.scout24.com/fileadmin/user_upload/IS24_WohnBarometer-Q4_2025_Miete.pdf' },
  { label: 'Stadt Leipzig, qualified Mietspiegel 2025–2027 (5,999 leases surveyed Oct 2024): city-wide average net cold rent €6.56/m²',
    url: 'https://www.leipzig.de/fileadmin/mediendatenbank/leipzig-de/Stadt/02.5_Dez5_Jugend_Soziales_Gesundheit_Schule/50_Sozialamt/Mietspiegel/Broschuere_zum_Leipziger_Mietspiegel_2025-2027_01.pdf' },
  { label: 'ImmoScout24: a 65–90 m² move with a removal company costs about €1,300–2,800',
    url: 'https://www.immobilienscout24.de/umzug/umzugskosten.html' },
  { label: 'Destatis: net rents +2.1% and nominal wages +4.2% in 2025',
    url: 'https://www.destatis.de/DE/Presse/Pressemitteilungen/2026/01/PD26_019_611.html' },
  { label: 'SOEP (Erlinghagen, Kern & Stein 2019): moving within Germany raises life satisfaction lastingly',
    url: 'https://www.diw.de/de/diw_01.c.679054.de/publikationen/soeppapers/2019_1046/internal_migration_social_strati_cation_and_dynamic_effects_on_subjective_well_being.html' },
]

// Buy vs rent on unrecoverable costs (money neither side gets back): the
// buyer's mortgage repayments build equity and are savings, not costs; the
// deposit isn't lost either, so its cost is the return the renter earns on it.
// The engine has no end-of-horizon payout, so appreciation is booked yearly.
const FLAT_PRICE = 400_000
const PRICE_TO_RENT = 24.4            // Postbank Wohnatlas 2026: national average 2025
const FLAT_RENT = Math.round(FLAT_PRICE / PRICE_TO_RENT / 100) * 100
const DEPOSIT = FLAT_PRICE * 0.2
const MORTGAGE_RATE = 0.0404          // Bundesbank, 10y+ fixed, Aug 2026

const BUY_RENT_SOURCES: { label: string; url: string }[] = [
  { label: 'Bundesbank MFI statistics: 4.04% effective rate on new housing loans fixed for 10+ years (Aug 2026)',
    url: 'https://api.statistiken.bundesbank.de/rest/data/BBIM1/M.DE.B.A2C.P.R.A.2250.EUR.N?format=csv' },
  { label: 'Purchase costs: land transfer tax 3.5–6.5% by state, notary + land registry 1.5–2%, buyer\'s agent share typically 3.57% (VR Banken)',
    url: 'https://www.vr.de/privatkunden/ihre-ziele/haus-kaufen/hauskauf-nebenkosten.html' },
  { label: 'Postbank Wohnatlas 2026 (HWWI): flats cost 24.4 years of net rent on average in 2025',
    url: 'https://www.baulinks.de/webplugin/2026/0929.php4' },
  { label: 'Destatis house price index: +82% from 2010 to 2025 (≈4.1%/year), with −8.5% in 2023',
    url: 'https://www.destatis.de/DE/Themen/Wirtschaft/Preise/Baupreise-Immobilienpreisindex/Tabellen/Haeuserpreise-Bauland.html' },
  { label: 'Destatis: net rents +2.1% in 2025',
    url: 'https://www.destatis.de/DE/Presse/Pressemitteilungen/2026/01/PD26_019_611.html' },
  { label: 'Upkeep: 1–1.5% of the building value a year (Interhyp)',
    url: 'https://www.interhyp.de/ratgeber/sanieren-und-renovieren/instandhaltungskosten-berechnen/' },
  { label: 'Bundesbank: 10-year Bund yield 3.64% (Sep 2026), the safe return on a deposit kept invested',
    url: 'https://api.statistiken.bundesbank.de/rest/data/BBSIS/M.I.ZST.ZI.EUR.S1311.B.A604.R10XX.R.A.A._Z._Z.A?format=csv' },
]

// Freelance vs employed, both as income before income tax, after health/care
// insurance and business costs. Pension contributions count as the person's
// own saving on both sides (the employer's half included), so they aren't
// deducted; income tax is left out because it applies to both similarly.
const DEV_GROSS = 5_260 * 12          // Lohnspiegel: software developer after 10 years
const EMPLOYEE_HEALTH = 0.0875 + 0.021 + 0.013   // health ½(14.6%+2.9%), care ≈2.1%, unemployment 1.3%
const FREELANCE_REVENUE = 6_000 * 12  // Freelancer-Kompass 2026: IT median ≈ €6,000/month full-time
const FREELANCE_COSTS = 783 * 12      // Freelancer-Kompass 2026: business costs per month
const FREELANCE_HEALTH = 0.1729 + 0.042           // TK 2026: health incl. sick pay + care (childless), both halves

const FREELANCE_SOURCES: { label: string; url: string }[] = [
  { label: 'freelancermap Freelancer-Kompass 2026 (n=620 IT freelancers): median €95/hour, monthly revenue around €6,000 full-time',
    url: 'https://www.freelancermap.de/blog/?p=71635' },
  { label: 'Freelancer-Kompass 2026: business costs €783/month; 24% had projects on fewer than 50 days last year',
    url: 'https://www.freelancermap.de/blog/?p=71117' },
  { label: 'Hans-Böckler-Stiftung Lohnspiegel (2025): software developers earn ~€5,260/month after 10 years',
    url: 'https://www.boeckler.de/fpdf/HBS-009284/ap_lohnspiegel_softwareentwickler.pdf' },
  { label: "StepStone Gehaltsreport 2026 (1.3M salary records), cross-check: IT occupations earn a median €66,750/year gross across all experience levels, close to the Lohnspiegel's ~€63K",
    url: 'https://www.stepstone.de/e-recruiting/hr-wissen/gehalt/gehaltsreport-deutschlands-gehaelter-im-fokus/' },
  { label: 'Social insurance rates and ceilings 2026 (krankenkassen.de)',
    url: 'https://www.krankenkassen.de/gesetzliche-krankenkassen/system-gesetzliche-krankenversicherung/sozialversicherung-rechengroessen-beitragsbemessungsgrenze-versicherungspflichtgrenze/rechengroessen-2026/' },
  { label: 'Techniker Krankenkasse 2026: self-employed pay both halves, 17.29% health + 4.2% care (childless)',
    url: 'https://www.tk.de/techniker/leistungen-und-mitgliedschaft/informationen-versicherte/veraenderung-berufliche-situation/versichert-als-selbststaendige/beitragssaetze-und-beitraege/hoehe-mindest-und-hoechstbeitraege-fuer-selbststaendige-2007380' },
]

/** Homepage example: the sourced two-option Master's decision over a 20-year
 *  career horizon, where the honest answer is genuinely close. */
export const HOMEPAGE_EXAMPLE = {
  key: 'masters-or-keep-working',
  h1: "Master's or keep working?",
  horizonYears: 20,
  // Engine output at HERO_SEED / 1,000 runs, verified by `npm run hero:check`.
  heroResult: { winner: 'Keep working', pct: '58%', edge: '€7K', risk: '0%' },
}

export function buildHomepageExample(): SeedDecision {
  return {
    name: "Master's or keep working?",
    horizon_years: HOMEPAGE_EXAMPLE.horizonYears,
    discount_rate: 0.05,
    risk_aversion: 0.002,
    choices: [
      ch('Keep working', 'Stay in your job with a bachelor\'s. Salary and raise are illustrative: use your own.', [
        v("Salary (bachelor's)", 'income', 'per_step', '€/year', N(BASE_SALARY, 5_000)),
        v('Annual raise', 'income', 'cumulative', '€/year', N(2_000, 800)),
      ]),
      ch("Do a Master's", MASTERS_NOTE, mastersVariables()),
    ],
  }
}

// ─── SEO template seeds ───────────────────────────────────────────────────────
// Used by /decisions/:slug pages. Variables use EUR as base currency.

export interface TemplateSeedMeta {
  slug: TemplateSlug
  title: string
  h1: string
  description: string
  // The engine's real output for this seed at HERO_SEED / 1,000 runs, so the
  // card is reproducible. Verified by `npm run hero:check` (scripts/hero-results.mjs);
  // re-run it after changing the engine, the seed scenario or these values.
  heroResult: { winner: string; pct: string; edge: string; risk: string }
  /** Where the scenario's numbers come from (shown on the page). */
  sources?: { label: string; url: string }[]
  /** Assumptions in the scenario that have no source, said out loud. */
  unsourced?: string
}

// Fixed RNG seed behind every heroResult card (see scripts/hero-results.mjs).
export const HERO_SEED = 42

export const TEMPLATE_SEEDS: TemplateSeedMeta[] = [
  {
    slug: 'should-i-take-the-new-job',
    title: 'Should I take the new job? Model it with Monte Carlo — LifeOdds',
    h1: 'Should I take the new job?',
    description: 'Stop agonising. Run 1,000 simulated futures on your job decision — salary, raises, layoff risk — and see which choice actually wins.',
    heroResult: { winner: 'Take the new job', pct: '89%', edge: '€31K', risk: '0%' },
    sources: JOB_SOURCES,
    unsourced: 'The offer (+10%) and your current salary are placeholders for your own numbers. A new hire\'s higher ' +
      'layoff risk (3% vs 2% a year here) is an assumption. Jobs end far more often in their first year (BA), but that counts ' +
      'quits and expiring contracts too; we found no German figure for how often employers let new hires go.',
  },
  {
    slug: 'masters-vs-bootcamp-vs-stay',
    title: "Master's vs bootcamp vs stay? Run the numbers — LifeOdds",
    h1: "Master's, bootcamp, or stay?",
    description: "Model 10-year NPV across all three paths. Factor in tuition, salary uplift, opportunity cost, and career risk — then let 1,000 futures vote.",
    heroResult: { winner: 'Stay & self-learn', pct: '98%', edge: '€58K', risk: '0%' },
    sources: [...MASTERS_SOURCES, ...BOOTCAMP_SOURCES],
    unsourced: 'The bootcamp graduate\'s faster raises (€2.8K/year) are an assumption, and the bootcamp path ' +
      'assumes you land a developer job: Le Wagon reports ~90% do within 6 months, an independent LinkedIn ' +
      'check of London graduates found ~56–70%. The current salary and raises are placeholders for yours.'
  },
  {
    slug: 'should-i-move-cities',
    title: 'Should I move cities? Simulate the decision — LifeOdds',
    h1: 'Should I move cities?',
    description: 'Compare take-home pay, rent and moving costs across two cities (Leipzig → Munich as the worked example). See which wins more futures before you sign the lease.',
    heroResult: { winner: 'Stay in Leipzig', pct: '100%', edge: '€39K', risk: '0%' },
    sources: MOVE_SOURCES,
    unsourced: 'Take-home pay is approximated from gross (≈64% in Leipzig, ≈62% in Munich; single, tax class I). ' +
      'The flat (65 m²) is illustrative. Life satisfaction ' +
      'isn\'t scored: the SOEP finding is that movers gain, but by how much is yours to set.',
  },
  {
    slug: 'buy-vs-rent',
    title: 'Buy vs rent? Run the Monte Carlo — LifeOdds',
    h1: 'Buy or rent?',
    description: 'Model house price appreciation, mortgage cost, opportunity cost of deposit, and rental inflation. Which choice wins over your time horizon?',
    heroResult: { winner: 'Buy', pct: '74%', edge: '€43K', risk: '86%' },
    sources: BUY_RENT_SOURCES,
    unsourced: 'House prices are assumed to grow 3% a year (between inflation, ~2.2%, and the 2010–2025 boom, ~4.1%), ' +
      'and the renter\'s deposit to earn 5% a year (between the Bund yield and long-run equities). Both decide ' +
      'the result: set your own. Both sides pay similar housing costs otherwise (both pay utilities, buyers also save ' +
      'through repayments).',
  },
  {
    slug: 'go-freelance-vs-stay-employed',
    title: 'Go freelance vs stay employed? Simulate both — LifeOdds',
    h1: 'Go freelance or stay employed?',
    description: 'Model day rates, client risk, holiday cost, pension gap, and income volatility. See whether independence is worth it — in expected value terms.',
    heroResult: { winner: 'Stay employed', pct: '87%', edge: '€48K', risk: '0%' },
    sources: FREELANCE_SOURCES,
    unsourced: 'Both sides are compared before income tax, which hits them similarly. Autonomy and security aren\'t ' +
      'scored: set your own wellbeing values. Revenue varies widely (a quarter of freelancers were busy on fewer ' +
      'than 50 days last year), which the ±€20K spread only roughly captures.',
  },
]

export function buildTemplateSeed(slug: TemplateSlug): SeedDecision {
  switch (slug) {
    case 'should-i-take-the-new-job':
      return {
        name: 'Should I take the new job?',
        horizon_years: 5,
        discount_rate: 0.05,
        risk_aversion: 0.002,
        choices: [
          ch('Stay at current job', 'Known pay, raises in line with the market, full dismissal protection.', [
            v('Base salary', 'income', 'per_step', '€/year', N(65_000, 2_000)),
            v('Annual raise (+4.2%, Destatis)', 'income', 'cumulative', '€/year', N(Math.round(65_000 * WAGE_GROWTH), 1_000)),
            v('Layoff risk (calibrated to BA data)', 'risk', 'per_step', 'prob/year', B(LAYOFF_P)),
            v('Job satisfaction (1-10)', 'wellbeing', 'per_step', 'score', N(6, 1.5)),
          ]),
          ch('Take the new job', 'A +10% offer. Switchers gain +3.6% on average in the same occupation, but the spread is wide.', [
            v('New salary (offer +10%)', 'income', 'per_step', '€/year', N(71_500, 2_000)),
            v('Annual raise (+4.2%, Destatis)', 'income', 'cumulative', '€/year', N(Math.round(71_500 * WAGE_GROWTH), 1_000)),
            v('Layoff risk (new hire, assumption)', 'risk', 'per_step', 'prob/year', B(0.03)),
            v('Job satisfaction (1-10, +1.1 at first, fading)', 'wellbeing', 'per_step', 'score', N(6.5, 1.5)),
          ]),
        ],
      }
    case 'masters-vs-bootcamp-vs-stay':
      return {
        name: "Master's, bootcamp, or stay?",
        horizon_years: 10,
        discount_rate: 0.05,
        risk_aversion: 0.002,
        choices: [
          ch('Stay & self-learn', 'Keep earning, upskill on the side.', [
            v('Current salary', 'income', 'per_step', '€/year', N(BASE_SALARY, 5_000)),
            v('Annual raise', 'income', 'cumulative', '€/year', N(2_000, 800)),
          ]),
          ch("Do a Master's", MASTERS_NOTE, [
            ...mastersVariables(),
            v('Employability uplift (wellbeing proxy, subjective: set your own)', 'wellbeing', 'per_step', 'score', N(2, 1)),
          ]),
          ch('Do a bootcamp', BOOTCAMP_NOTE, [
            v('Bootcamp cost', 'one_time_cost', 'once', '€', N(10_000, 2_500)),
            v('About 3 months without salary (course + job search)', 'one_time_cost', 'once', '€', N(14_000, 3_000)),
            v('Junior developer salary after the bootcamp', 'income', 'per_step', '€/year', N(BOOTCAMP_SALARY, 6_000)),
            v('Annual raise (assumption)', 'income', 'cumulative', '€/year', N(2_800, 1_000)),
          ]),
        ],
      }
    case 'should-i-move-cities':
      return {
        name: 'Should I move cities?',
        horizon_years: 7,
        discount_rate: 0.05,
        risk_aversion: 0.002,
        choices: [
          ch('Stay in Leipzig', 'Median Leipzig pay, your existing Leipzig lease on a 65 m² flat.', [
            v('Take-home pay (≈64% of €' + Math.round(LEIPZIG_GROSS / 1000) + 'K gross)', 'income', 'per_step', '€/year',
              N(Math.round(LEIPZIG_GROSS * 0.64 / 100) * 100, 2_000)),
            v('Pay rise (+4.2%, Destatis)', 'income', 'cumulative', '€/year', N(Math.round(LEIPZIG_GROSS * 0.64 * WAGE_GROWTH), 400)),
            v('Rent (65 m² at €6.56/m², Mietspiegel)', 'cost', 'per_step', '€/year', N(LEIPZIG_RENT, 500)),
            v('Rent increase (+2.1%, Destatis)', 'cost', 'cumulative', '€/year', N(Math.round(LEIPZIG_RENT * RENT_GROWTH), 60)),
          ]),
          ch('Move to Munich', 'Munich pays the median 42% more, but a mover gains under half of that; new-contract Munich rent.', [
            v('Take-home pay (≈62% of €' + Math.round(MUNICH_GROSS / 1000) + 'K gross)', 'income', 'per_step', '€/year',
              N(Math.round(MUNICH_GROSS * 0.62 / 100) * 100, 3_000)),
            v('Pay rise (+4.2%, Destatis)', 'income', 'cumulative', '€/year', N(Math.round(MUNICH_GROSS * 0.62 * WAGE_GROWTH), 500)),
            v('Rent (65 m² at €19.92/m²)', 'cost', 'per_step', '€/year', N(MUNICH_RENT, 1_000)),
            v('Rent increase (+2.1%, Destatis)', 'cost', 'cumulative', '€/year', N(Math.round(MUNICH_RENT * RENT_GROWTH), 120)),
            v('Removal company (~400 km)', 'one_time_cost', 'once', '€', N(2_200, 400)),
          ]),
        ],
      }
    case 'buy-vs-rent':
      return {
        name: 'Buy or rent?',
        horizon_years: 10,
        discount_rate: 0.04,
        risk_aversion: 0.003,
        choices: [
          ch('Rent', 'Rent a comparable flat and keep the €80K deposit invested.', [
            v('Rent (€400K ÷ 24.4)', 'cost', 'per_step', '€/year', N(FLAT_RENT, 800)),
            v('Rent increase (+2.1%, Destatis)', 'cost', 'cumulative', '€/year', N(Math.round(FLAT_RENT * RENT_GROWTH), 150)),
            v('Return on the €80K deposit (5%, assumption)', 'income', 'per_step', '€/year', N(DEPOSIT * 0.05, DEPOSIT * 0.12)),
          ]),
          ch('Buy', 'A €400K flat with 20% down; repayments build equity, so only interest, upkeep and purchase costs are lost.', [
            v('Purchase costs (tax, notary, agent ≈11%)', 'one_time_cost', 'once', '€', N(FLAT_PRICE * 0.11, 8_000)),
            v('Mortgage interest (4.04% on a falling €320K balance)', 'cost', 'per_step', '€/year',
              N(Math.round(DEPOSIT * 4 * MORTGAGE_RATE * 0.88 / 100) * 100, 800)),
            v('Upkeep + property tax', 'cost', 'per_step', '€/year', N(3_800, 800)),
            v('House price change (3%, assumption)', 'income', 'per_step', '€/year', N(FLAT_PRICE * 0.03, FLAT_PRICE * 0.06)),
          ]),
        ],
      }
    case 'go-freelance-vs-stay-employed':
      return {
        name: 'Go freelance or stay employed?',
        horizon_years: 5,
        discount_rate: 0.05,
        risk_aversion: 0.003,
        choices: [
          ch('Stay employed', 'A software developer\'s salary; the employer pays half of every social contribution.', [
            v('Gross salary (Lohnspiegel)', 'income', 'per_step', '€/year', N(DEV_GROSS, 4_000)),
            v("Employer's half of pension contributions (9.3%)", 'income', 'per_step', '€/year', N(Math.round(DEV_GROSS * 0.093), 300)),
            v('Your health, care + unemployment insurance', 'cost', 'per_step', '€/year', N(Math.round(DEV_GROSS * EMPLOYEE_HEALTH), 300)),
            v('Layoff risk (calibrated to BA data)', 'risk', 'per_step', 'prob/year', B(LAYOFF_P)),
          ]),
          ch('Go freelance', 'Median IT freelance revenue; you pay both halves of health insurance and your own costs.', [
            v('Revenue (≈€6K/month, Freelancer-Kompass)', 'income', 'per_step', '€/year', N(FREELANCE_REVENUE, 20_000)),
            v('Business costs (€783/month)', 'cost', 'per_step', '€/year', N(FREELANCE_COSTS, 2_000)),
            v('Health + care insurance, both halves', 'cost', 'per_step', '€/year',
              N(Math.round((FREELANCE_REVENUE - FREELANCE_COSTS) * FREELANCE_HEALTH / 100) * 100, 1_500)),
          ]),
        ],
      }
    default:
      return buildExplainerSeed('monte_carlo')
  }
}
